import { ParticleBackground } from "@/components/ParticleBackground";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function SensoryAidsPage() {
  return (
    <>
      <SEOHead 
        title="Sensory Aids to Help With Mobility and Calming | Samboards" 
        description="Learn how Samboards sensory aids help with mobility, calming, concentration, hand control, and memory building through tactile stimulation." 
        canonical="/sensory-aids" 
        keywords="sensory aids, mobility aids, calming tools, dementia sensory tools, tactile stimulation, fine motor skills, anxiety reduction"
      />
      <BreadcrumbSchema 
        items={[
          { name: "Home", url: "/" },
          { name: "Products", url: "/shop" },
          { name: "Sensory Aids", url: "/sensory-aids" }
        ]} 
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />

        <Page>
          <section className="pb-24">
            <div className="max-w-4xl mx-auto">
              <h1 className="font-heading text-[40px] sm:text-[48px] lg:text-[56px] leading-[1.05] page-title-gradient mb-12">
                Sensory Aids to Help With Mobility and Calming
              </h1>

              <div className="space-y-12">
                {/* Introduction */}
                <div className="prose prose-invert max-w-none">
                  <p className="text-base md:text-lg text-slate-200/80 leading-relaxed">
                    Our range of products have been developed to meet the needs of a wide range of people. They are robust, silent, discrete, safe and easy to clean. Each texture creates a different feel, some people may prefer one texture to another depending on what mood they are in or want to create. They can be a distraction or an activity depending on how you use them. They can improve small motor skills by using your fingers and hands which increases large motor skills as your arm muscles are used. The increase in the sensory information through your nervous system then increases brain activity and blood flow. You may feel more in control of your emotions, more able to concentrate in the moment or be able to lower your anxiety levels. Samboards would like to encourage you to swap between textures for extra engagement.
                  </p>
                </div>

                {/* Dementia Section */}
                <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                  <h2 className="text-2xl sm:text-3xl font-heading bg-gradient-to-r from-emerald-200 via-teal-200 to-sky-300 bg-clip-text text-transparent mb-6">
                    How our products help people with Dementia
                  </h2>
                  <p className="text-base text-slate-200/80 leading-relaxed mb-4">
                    Discover Samboard Sensory Pad
                  </p>
                  <p className="text-base text-slate-200/80 leading-relaxed mb-4">
                    The Samboard can be used for enjoyment or as a distraction. Touching and feeling the board exercises your hands, increases messages to your brain, and releases natural chemicals which can help a person feel more motivated, and improve the sense of wellbeing. Benefits reported by other users include: increased concentration and focus, reduced anxiety, improved circulation and fine motor skills, a greater sense of calm and well being.
                  </p>
                  <p className="text-base text-slate-200/80 leading-relaxed">
                    Some of the textures are designed to recreate images of nature which are known to be soothing. When the texture is felt, different feedback or feelings are created, the blood flow to the outside of the brain increases, and hormones are released. The sensory needs can be met by one or more of these textures. By touching the board and finding new textures to explore the body is releasing dopamine, which is a "feel good, stimulating" hormone. The act of touch also produces messages through the nervous system which can then move blood in the brain away from the amygdala reducing anxiety and stress.
                  </p>
                </div>

                {/* Benefits Grid */}
                <div className="grid gap-8 md:grid-cols-3">
                  {/* Calming */}
                  <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] transition-all duration-300 hover:shadow-[0_0_40px_rgba(16,185,129,0.25)] hover:border-emerald-400/30">
                    <h3 className="text-xl font-semibold text-slate-100 mb-4">
                      Calming and improves concentration
                    </h3>
                    <p className="text-base text-slate-300/70 leading-relaxed">
                      While using the sensory pad people may feel calmer and more in control of their emotions. A calm mind can make learning easier and make better choices. They can feel more engaged and interested the world around them.
                    </p>
                  </div>

                  {/* Motor Skills */}
                  <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] transition-all duration-300 hover:shadow-[0_0_40px_rgba(16,185,129,0.25)] hover:border-emerald-400/30">
                    <h3 className="text-xl font-semibold text-slate-100 mb-4">
                      Improves hand control and small motor skills
                    </h3>
                    <p className="text-base text-slate-300/70 leading-relaxed">
                      By using your hands and fingers to touch textures you might like can give you a positive feeling, improving your mood and your small motor skills. This can lead to better hand control for other tasks like feeding yourself or doing up buttons.
                    </p>
                  </div>

                  {/* Memory */}
                  <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] transition-all duration-300 hover:shadow-[0_0_40px_rgba(16,185,129,0.25)] hover:border-emerald-400/30">
                    <h3 className="text-xl font-semibold text-slate-100 mb-4">
                      Helps memory building and retrieval
                    </h3>
                    <p className="text-base text-slate-300/70 leading-relaxed">
                      The tactile experience can also help in memory building, memory storing, and memory retrieval. By experiencing something different and new to you, enables building new pathways in your brain. The more pathways you have in your brain the more accessible routes your thoughts can take, which makes brain aging and memory loss less noticeable. If your tactile sensors have been under stimulated and that part of your brain has reduced its function, it is possible to rebuild your sensitivity and the area of the brain it lives in.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Page>
      </div>
    </>
  );
}
